import moment from "moment";
import { forwardRef, useEffect, useRef, useState } from "react";
import { Button, Form, InputGroup } from "react-bootstrap";
import styled from "styled-components";
import { addAutoHyphen } from "../../common/util";
import { useDispatch } from "react-redux";
import { showAlert } from "../../../reducer/alertSlice";
import Loading from "../organisms/Loading";
import { useCertification, useRequestCertification } from "../../common/hook";
import { hideConfirm, showConfirm } from "../../../reducer/confirmSlice";

const Wrap = styled.div`
    width: 100%;
    border-radius: 4px;
`;

const Timer = styled.div`
    position: absolute;
    top: 50%;
    right: 110px;
    transform: translateY(-50%);
    z-index: 5;
    font-size: 14px;
`;

const MobileAuth = forwardRef(({addValue, removeValue, addedArr=[], certifiedNum = null, type="", id=""},  ref) => {
    const timer = useRef();
    const timeLimitSec = 180;
    // const timeLimitSec = 10; // test
    const timeLimit = moment.utc(timeLimitSec * 1000).format("mm:ss");
    const [sendReq, setSendReq] = useState(false); // 인증 요청 여부
    const [authReq, setAuthReq] = useState(false); // 인증 확인 버튼 활성화 여부
    const [certified, setCertified] = useState(certifiedNum); // 인증 완료
    const [startTimer, setStartTimer] = useState(false);
    const [showTimer, setShowTimer] = useState(timeLimit);
    const [insertHypen, setInsertHypen] = useState(certifiedNum ? certifiedNum : "");
    const [loading, setLoading] = useState(false);
    const [certificationNo, setCertificationNo] = useState("");

    const requestCertification = useRequestCertification(insertHypen, type, id);
    const certification = useCertification(insertHypen, certificationNo, () => {handleClickReqCerti()});
    const dispatch = useDispatch();

    const handleClickOnCertification = async () => {
        if (certificationNo != "") {
            setLoading(true);
            const result = await certification.refetch();
            if (result.data) {
                if (!result.isError) {
                    if (result.data.status == 200 && result.data.data.code == 0) {
                        setSendReq(false);
                        setCertified(insertHypen);
                        clearInterval(timer.current);
                        setCertificationNo("");
                    }
                }
            }
            setLoading(false);
        } else {
            dispatch(showAlert({
                msg: "인증번호를 입력해주세요."
            }))
        }

    }

    const handleClickReqCerti = async () => {
        let isExists = false;
        // 중복 검사
        // 1. 해당 페이지에서 추가된 번호와 동일한지 확인
        addedArr.forEach((el, idx) => {
            if (el == insertHypen) {
                isExists = true;
            }
        });

        if (isExists) {
            dispatch(showAlert({
                msg: <>이미 등록 또는 중복된 휴대폰 번호가 있습니다.<br/>동일 휴대폰 번호로 여러 계정에 등록 불가합니다.</>
            }));
            setAuthReq(false);
        } else {
            if (!type) {
                dispatch(showAlert({
                    msg: "요청 중 오류가 발생했습니다."
                }))
            } else {
                setLoading(true);
                clearInterval(timer.current);

                const result = await requestCertification.refetch();
                if (result.data) {
                    if (result.data.status == 200) {
                        setAuthReq(true);
                        dispatch(showAlert({
                            msg: "인증번호가 발송되었습니다."
                        }));
            
                        let sec = 0;
                        setSendReq(true);
                        setStartTimer(true);
                        setShowTimer(moment(timeLimit, "mm:ss").format("mm:ss"));
                        timer.current = setInterval(() => {
                            sec--;
                            setShowTimer(moment(timeLimit, "mm:ss").add(sec, "s").format("mm:ss"));
                            if (sec == timeLimitSec * -1) {
                                clearInterval(timer.current);
                                setShowTimer(timeLimit);
                                setStartTimer(false);
                                dispatch(showConfirm({
                                    msg: <>인증번호 입력 시간이 초과되었습니다.<br/>재요청 후 다시 입력해주세요.</>,
                                    btnText: "재요청", 
                                    func: () => {
                                        dispatch(hideConfirm());
                                        handleClickReqCerti();
                                    }
                                }))
                            }
                        },1000);
                    }
                }
                setLoading(false);
            }
        }
    }

    const resetState = () => {
        setCertified(null);
        setSendReq(false);
        setAuthReq(false);
        setStartTimer(false);
        clearInterval(timer.current);
    }
    
    useEffect(() => {
        if (certifiedNum) {
            setInsertHypen(addAutoHyphen(certifiedNum));
            addValue(addAutoHyphen(certifiedNum));
        }

        return () => {
            clearInterval(timer.current);
        }
    },[]);

    useEffect(() => {
        if (certified) {
            addValue(insertHypen);
        }
    },[certified]);

    return (
        <Wrap>
            {
                loading && 
                <Loading layer={true}/>
            }
            <InputGroup>
                <Form.Control
                    placeholder="-없이 숫자만 입력하세요"
                    maxLength={13}
                    ref={ref}
                    onInput={(e) => {
                        const num = e.target.value;
                        setInsertHypen(addAutoHyphen(num));
                        // 내용 수정 시 저장 된 값 삭제
                        if (certified) {
                            removeValue(certified);
                            resetState();
                        }
                        if (authReq) {
                            resetState();
                        }
                    }}
                    value={insertHypen}
                />
                <Button variant={certified ? "secondary" : "primary"} style={{width: "100px"}} 
                    disabled={insertHypen.length < 12 || startTimer || certified} 
                    onClick={() => {
                        handleClickReqCerti();
                    }}
                >
                    {
                    sendReq
                    ?
                    "재요청"
                    :
                    certified ? 
                    "인증 완료"
                    :
                    "인증 요청"
                    }
                </Button>
            </InputGroup>
            {
            (sendReq && !certified) &&
            <InputGroup style={{marginTop: "3px"}}>
                <Form.Control
                    placeholder="인증번호를 입력하세요"
                    onInput={(e) => {
                        setCertificationNo(e.target.value.replace(/[^0-9]/g, ""));
                    }}
                    value={certificationNo}
                />

                <Timer className="showTimer">{showTimer}</Timer>
                <Button variant="primary" style={{width: "100px"}} disabled={!authReq} 
                        onClick={() => {
                            handleClickOnCertification();
                        }}
                >
                    인증 확인
                </Button>
            </InputGroup>
            }
        </Wrap>
    )
})

export default MobileAuth